import { Link } from "react-router-dom";
import {
  Building2,
  Compass,
  HeartHandshake,
  ShieldCheck,
  Rocket,
  Users,
  Search,
  CalendarCheck,
  MailCheck,
  Mail,
  Phone,
  ArrowRight,
} from "lucide-react";
import "./MarketingPage.css";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL } from "../../config/company";

export default function AboutPage() {
  return (
    <div className="mkt-shell">
      <div className="mkt-container">
        {/* ========= HERO ========= */}
        <section className="mkt-hero">
          <div className="mkt-hero__inner">
            <div className="mkt-hero__icon">
              <Building2 size={36} strokeWidth={2.2} />
            </div>
            <div className="mkt-hero__copy">
              <h1>À propos de ProMeetService</h1>
              <p>
                Une plateforme française dédiée aux professionnels du service et de l'automobile.
                Notre mission : rendre la prise de rendez-vous, la gestion d'activité et la
                mise en relation B2B aussi simples et fluides que possible.
              </p>
              <div className="mkt-hero__actions">
                <Link className="mkt-btn mkt-btn--primary" to="/search">
                  <Search size={16} /> Trouver un pro
                </Link>
                <Link className="mkt-btn mkt-btn--ghost" to="/auth/signup/pro">
                  <Rocket size={16} /> Devenir pro
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ========= Chiffres clés ========= */}
        <section className="mkt-stats">
          <div className="mkt-stat">
            <div className="mkt-stat__icon"><Users size={20} /></div>
            <div className="mkt-stat__value">+1 200</div>
            <div className="mkt-stat__label">Professionnels</div>
          </div>
          <div className="mkt-stat">
            <div className="mkt-stat__icon"><CalendarCheck size={20} /></div>
            <div className="mkt-stat__value">+25 000</div>
            <div className="mkt-stat__label">Rendez-vous traités</div>
          </div>
          <div className="mkt-stat">
            <div className="mkt-stat__icon"><HeartHandshake size={20} /></div>
            <div className="mkt-stat__value">98 %</div>
            <div className="mkt-stat__label">Clients satisfaits</div>
          </div>
          <div className="mkt-stat">
            <div className="mkt-stat__icon"><ShieldCheck size={20} /></div>
            <div className="mkt-stat__value">100 %</div>
            <div className="mkt-stat__label">Pros vérifiés</div>
          </div>
        </section>

        {/* ========= Notre mission ========= */}
        <section className="mkt-card mkt-card--accent">
          <h2><span className="mkt-h2-icon"><Compass size={18} /></span>Notre mission</h2>
          <p className="mkt-card-lead">
            Rendre l'accès aux services professionnels — et la gestion d'activité des
            indépendants — <strong>simple, rapide et premium</strong>.
          </p>
          <p>
            ProMeetService combine deux univers : un agenda intelligent pour les pros
            (rendez-vous, devis, factures, abonnements) et une marketplace dédiée aux
            missions automobiles B2B (carrosserie, jantes, convoyage, nettoyage…). Tout est
            pensé mobile-first, pour les professionnels qui bougent.
          </p>
        </section>

        {/* ========= Valeurs ========= */}
        <h2 className="mkt-section-title"><HeartHandshake size={20} /> Nos valeurs</h2>
        <section className="mkt-grid-2">
          <div className="mkt-feature">
            <div className="mkt-feature__icon"><ShieldCheck size={22} /></div>
            <div>
              <h3>Confiance & sécurité</h3>
              <p>Pros vérifiés (SIRET, identité, justificatifs) et plateforme conforme aux standards français.</p>
            </div>
          </div>
          <div className="mkt-feature mkt-feature--info">
            <div className="mkt-feature__icon"><Rocket size={22} /></div>
            <div>
              <h3>Rapidité & simplicité</h3>
              <p>Réservation en 3 clics, mission postée en 30 secondes, candidature pro en moins d'1 minute.</p>
            </div>
          </div>
          <div className="mkt-feature mkt-feature--violet">
            <div className="mkt-feature__icon"><Users size={22} /></div>
            <div>
              <h3>Proximité humaine</h3>
              <p>Une équipe française à l'écoute, des pros près de chez vous, un support réactif.</p>
            </div>
          </div>
          <div className="mkt-feature mkt-feature--success">
            <div className="mkt-feature__icon"><HeartHandshake size={22} /></div>
            <div>
              <h3>Transparence</h3>
              <p>Tarifs clairs, commission unique de 10 %, aucune surprise sur les factures.</p>
            </div>
          </div>
        </section>

        {/* ========= Comment ça marche ========= */}
        <h2 className="mkt-section-title"><Compass size={20} /> Comment ça marche</h2>
        <section className="mkt-steps">
          <div className="mkt-step">
            <h3>Recherchez</h3>
            <p>Trouvez un pro ou une mission selon votre besoin, votre ville et vos disponibilités.</p>
          </div>
          <div className="mkt-step">
            <h3>Réservez ou postulez</h3>
            <p>Réservation directe en ligne, ou candidature en 30 secondes pour les missions B2B.</p>
          </div>
          <div className="mkt-step">
            <h3>Suivez</h3>
            <p>Échangez, partagez vos photos, suivez l'avancement et restez informé en temps réel.</p>
          </div>
          <div className="mkt-step">
            <h3>Validez</h3>
            <p>Validez la prestation, recevez votre facture et notez l'intervention.</p>
          </div>
        </section>

        {/* ========= CTA ========= */}
        <section className="mkt-cta">
          <div>
            <h3>Une question, une suggestion ?</h3>
            <p>Notre équipe vous répond sous 24h ouvrées.</p>
          </div>
          <div className="mkt-cta__actions">
            <a className="mkt-btn" href={`mailto:${CONTACT_EMAIL}`}>
              <Mail size={16} /> {CONTACT_EMAIL}
            </a>
            <a className="mkt-btn mkt-btn--ghost" href={`tel:${CONTACT_PHONE_TEL}`}>
              <Phone size={16} /> {CONTACT_PHONE}
            </a>
            <Link className="mkt-btn mkt-btn--primary" to="/help">
              <MailCheck size={16} /> Centre d'aide <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
